import { makeAutoObservable } from "mobx"
import { useEffect, useMemo, useRef } from "react"

class FileDraggingStore {
  isDragging: boolean

  constructor() {
    this.isDragging = false

    makeAutoObservable(this)
  }

  setIsDragging(value: boolean) {
    this.isDragging = value
  }
}

export const useFileDragging = ({
  uploadFile,
  isEnabled = true,
}: {
  uploadFile: (file: File) => void
  isEnabled?: boolean
}) => {
  const state = useMemo(() => new FileDraggingStore(), [])
  const dragCounter = useRef(0)

  useEffect(() => {
    if (!isEnabled) return

    const preventDefaults = (e: DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
    }

    const handleDragEnter = (e: DragEvent) => {
      preventDefaults(e)
      if (!e.dataTransfer?.types.includes("Files")) return
      dragCounter.current++
      if (dragCounter.current === 1) {
        state.setIsDragging(true)
      }
    }

    const handleDragLeave = (e: DragEvent) => {
      preventDefaults(e)
      if (!e.dataTransfer?.types.includes("Files")) return
      dragCounter.current--
      if (dragCounter.current === 0) {
        state.setIsDragging(false)
      }
    }

    const handleDrop = (e: DragEvent) => {
      preventDefaults(e)
      state.setIsDragging(false)

      const files = e.dataTransfer.files
      if (files && files.length > 0) {
        uploadFile(files[0])
      }

      dragCounter.current = 0
    }

    window.addEventListener("dragenter", handleDragEnter)
    window.addEventListener("dragover", preventDefaults)
    window.addEventListener("dragleave", handleDragLeave)
    window.addEventListener("drop", handleDrop)

    return () => {
      window.removeEventListener("dragenter", handleDragEnter)
      window.removeEventListener("dragover", preventDefaults)
      window.removeEventListener("dragleave", handleDragLeave)
      window.removeEventListener("drop", handleDrop)
    }
  }, [isEnabled])

  return { isDragging: state.isDragging }
}
